<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 md:p-6 lg:p-8 font-sans text-slate-900 space-y-6">
    
    <!-- EN-TÊTE ET STATISTIQUES GLOBALES -->
    <header class="flex  lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
      <div class="space-y-1">
        <div class="flex items-center gap-2 text-xs font-semibold text-slate-400">
          <span>Medialink Solutions</span>
          <span>/</span>
          <span class="text-blue-600">Parc Kiosks</span>
        </div>
        <h1 class="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
          Supervision des Bornes Tactiles
        </h1>
      </div>

      <!-- Compteurs rapides du parc -->
      <div class="flex items-center gap-3">
        <div class="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs">
          <span class="text-slate-400 font-medium">Total :</span> <strong class="text-slate-900 font-bold">{{ kiosks.length }}</strong>
        </div>
        <div class="px-3.5 py-2 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700">
          <span class="font-medium">En ligne :</span> <strong class="font-bold">{{ onlineCount }}</strong>
        </div>
        <div class="px-3.5 py-2 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
          <span class="font-medium">Hors ligne :</span> <strong class="font-bold">{{ offlineCount }}</strong>
        </div>
      </div>
    </header>

    <!-- BARRE DE RECHERCHE ET FILTRES AVANCÉS -->
    <div class="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex  md:flex-row gap-4 items-center justify-between">
      
      <!-- Recherche textuelle -->
      <div class="w-full md:w-96 relative">
        <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </span>
        <input 
          type="text" 
          v-model="search" 
          placeholder="Rechercher par ID, Nom ou Pharmacie..." 
          class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 text-xs font-medium text-slate-700"
        >
      </div>

      <!-- Filtres de statut -->
      <div class="flex items-center gap-2 w-full md:w-auto justify-end">
        <select 
          v-model="statusFilter" 
          class="bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option value="all">🔍 Tous les statuts</option>
          <option value="active">🟢 En ligne uniquement</option>
          <option value="inactive">🔴 Hors ligne uniquement</option>
        </select>

        <select 
          v-model="itemsPerPage" 
          class="bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-blue-500"
        >
          <option :value="15">15 par page</option>
          <option :value="25">25 par page</option>
          <option :value="50">50 par page</option>
          <option :value="100">100 par page</option>
        </select>
      </div>
    </div>

    <!-- TABLEAU DE GESTION -->
    <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      
      <!-- État de chargement -->
      <div v-if="loading" class="p-8 space-y-4">
        <div v-for="n in 5" :key="n" class="flex items-center justify-between animate-pulse">
           <div class="flex gap-3 w-1/3">
             <div class="h-9 w-9 bg-slate-100 rounded-xl"></div>
             <div class="space-y-2 flex-1">
               <div class="h-3 bg-slate-100 rounded w-3/4"></div>
               <div class="h-2 bg-slate-100 rounded w-1/2"></div>
             </div>
           </div>
           <div class="h-3 bg-slate-100 rounded w-20"></div>
           <div class="h-8 bg-slate-100 rounded w-28"></div>
        </div>
      </div>

      <!-- Aucun résultat -->
      <div v-else-if="filteredKiosks.length === 0" class="p-16 text-center flex flex-col items-center justify-center">
        <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3">
          <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h3 class="text-slate-900 font-bold text-sm mb-1">Aucun kiosque trouvé</h3>
        <p class="text-slate-400 text-xs">Vérifiez vos filtres ou votre recherche.</p>
      </div>

      <!-- Liste avec Table -->
      <template v-else>
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-50/75 text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th class="px-6 py-4">Pharmacie / Officine</th>
                <th class="px-6 py-4">ID / Nom Borne</th>
                <th class="px-6 py-4 text-center">Statut</th>
                <th class="px-6 py-4">Dernière Synchronisation</th>
                <th class="px-6 py-4 text-right">Configuration Rapide</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-xs">
              <tr v-for="kiosk in paginatedKiosks" :key="kiosk.id" class="hover:bg-slate-50/60 transition-colors group">
                
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-800">{{ kiosk.agencyName }}</div>
                  <div class="text-[10px] text-slate-400 font-mono">Ref: {{ kiosk.agencyId?.substring(0,8) }}...</div>
                </td> 

                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100 shrink-0">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <div class="font-bold text-slate-900">{{ kiosk.name }}</div>
                      <div class="text-[10px] text-slate-400 font-mono">{{ kiosk.id }}</div>
                    </div>
                  </div>
                </td>

                <td class="px-6 py-4 text-center">
                  <span :class="statusClasses(kiosk.status)" class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border">
                    <span class="w-1.5 h-1.5 rounded-full bg-current"></span>
                    {{ kiosk.statusLabel }}
                  </span>
                </td>

                <td class="px-6 py-4 text-slate-500 font-medium tabular-nums">{{ kiosk.lastUpdate }}</td>
                
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-1.5 opacity-90 group-hover:opacity-100">
                    
                    <button @click="goToLayout(kiosk)" class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 rounded-xl transition-all shadow-sm" title="Éditer Layout">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>
                    </button>

                    <button @click="goToLanguages(kiosk)" class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-purple-600 hover:border-purple-200 hover:bg-purple-50 rounded-xl transition-all shadow-sm" title="Gérer les Langues">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                    </button>

                    <button @click="goToFamilyPack(kiosk)" class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 rounded-xl transition-all shadow-sm" title="Écran Family Pack">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </button>

                    <button @click="goToTicket(kiosk)" class="p-2 bg-white border border-slate-200 text-slate-500 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50 rounded-xl transition-all shadow-sm" title="Design du Ticket">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                    </button>

                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination avancée -->
        <div class="px-6 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            <span class="font-semibold text-slate-500">
               Affichage de <strong class="text-slate-800">{{ paginationStart }}</strong> à <strong class="text-slate-800">{{ paginationEnd }}</strong> sur <strong class="text-slate-800">{{ filteredKiosks.length }}</strong> bornes
            </span>
            <div class="flex items-center gap-2">
                <button 
                  @click="currentPage--" 
                  :disabled="currentPage === 1"
                  class="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 font-bold uppercase disabled:opacity-40 hover:bg-slate-50 transition-colors"
                >
                  Précédent
                </button>
                <span class="px-3 font-bold text-slate-700">
                  Page {{ currentPage }} / {{ totalPages || 1 }}
                </span>
                <button 
                  @click="currentPage++" 
                  :disabled="currentPage >= totalPages"
                  class="px-3.5 py-2 rounded-xl bg-blue-600 text-white font-bold uppercase disabled:opacity-40 hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Suivant
                </button>
            </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

const router = useRouter()
const API_BASE = 'https://api.datafuse.fr/api/super-admin'

const kiosks = ref([])
const search = ref('')
const statusFilter = ref('all')
const loading = ref(true)
const currentPage = ref(1)
const itemsPerPage = ref(25) // Par défaut 25 pour une meilleure vue d'ensemble

// Compteurs globaux
const onlineCount = computed(() => kiosks.value.filter(k => k.status === 'active').length)
const offlineCount = computed(() => kiosks.value.filter(k => k.status === 'inactive').length)

// Filtrage multi-critères (Recherche + Statut)
const filteredKiosks = computed(() => {
  return kiosks.value.filter(k => {
    const matchesSearch = !search.value || 
      k.id.toLowerCase().includes(search.value.toLowerCase()) || 
      k.name.toLowerCase().includes(search.value.toLowerCase()) ||
      (k.agencyName && k.agencyName.toLowerCase().includes(search.value.toLowerCase()))

    const matchesStatus = statusFilter.value === 'all' || k.status === statusFilter.value

    return matchesSearch && matchesStatus
  })
})

const totalPages = computed(() => Math.ceil(filteredKiosks.value.length / itemsPerPage.value))

const paginatedKiosks = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredKiosks.value.slice(start, end)
})

const paginationStart = computed(() => {
  if (filteredKiosks.value.length === 0) return 0
  return (currentPage.value - 1) * itemsPerPage.value + 1
})

const paginationEnd = computed(() => {
  const end = currentPage.value * itemsPerPage.value
  return end > filteredKiosks.value.length ? filteredKiosks.value.length : end
})

// Remise à zéro de la page si les filtres changent
watch([search, statusFilter, itemsPerPage], () => {
  currentPage.value = 1
})

const fetchKiosks = async () => {
  loading.value = true
  try {
    const token = localStorage.getItem("superAdminToken")
    const res = await fetch(`${API_BASE}/kiosks`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    
    if (!res.ok) throw new Error('Erreur API')

    const data = await res.json()
    
    kiosks.value = data.map(k => ({
      ...k,
      status: k.online ? "active" : "inactive",
      statusLabel: k.online ? "En ligne" : "Hors ligne",
      lastUpdate: new Date(k.lastUpdate).toLocaleDateString('fr-FR', { 
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute:'2-digit' 
      })
    }))
  } catch (err) {
    console.error(err)
    toast.error("Impossible de charger les kiosques")
  } finally {
    loading.value = false
  }
}

const statusClasses = (status) => {
  if (status === 'active') return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  return 'bg-rose-50 text-rose-700 border-rose-200'
}

// Navigation
const goToLayout = (kiosk) => router.push({ name: 'KioskLayout', params: { kioskId: kiosk.agencyId } })
const goToLanguages = (kiosk) => router.push({ name: 'LanguageSelection', params: { kioskId: kiosk.agencyId } })
const goToFamilyPack = (kiosk) => router.push({ name: 'FamilyPackEditor', params: { kioskId: kiosk.agencyId } })
const goToTicket = (kiosk) => router.push({ name: 'TicketDesigner', params: { kioskId: kiosk.agencyId } })

onMounted(() => {
  fetchKiosks()
})
</script>